import type { ReactNode } from 'react';
import { AlertTriangle, Check, ShieldAlert, Snowflake } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Etiqueta } from '@/features/tickets/badges';
import {
  ROTULO_DE_DESFECHO,
  ROTULO_DE_ENFORCEMENT,
  ROTULO_DE_EXCECAO,
  ROTULO_DE_JANELA,
  ROTULO_DE_RISCO,
  ROTULO_DE_TIPO_DE_MUDANCA,
  type Desfecho,
  type Enforcement,
  type NivelDeRisco,
  type SituacaoDeExcecao,
  type SituacaoDeJanela,
  type TipoDeMudanca,
} from '@/features/changes/tipos';

/**
 * As peças que as telas de mudança compartilham.
 *
 * Toda etiqueta traz **a palavra**, nunca só a cor: num calendário de
 * manutenção, "bloqueado" e "atenção" precisam ser legíveis para quem não
 * distingue vermelho de âmbar.
 */

// ── etiquetas ──────────────────────────────────────────────────────────────

/**
 * O tipo da mudança, com o peso visual que ele merece.
 *
 * Emergencial é destrutivo de propósito: é o único dos três que **já
 * aconteceu** quando o comitê olha, e a tela não deve deixar isso passar como
 * mais uma escolha de combo.
 */
const ESTILO_DE_TIPO: Record<TipoDeMudanca, string> = {
  STANDARD: 'bg-success/12 text-success ring-success/25',
  NORMAL: 'bg-primary/12 text-primary ring-primary/25',
  EMERGENCY: 'bg-destructive/12 text-destructive ring-destructive/25',
};

export function EtiquetaDeTipoDeMudanca({ tipo }: { tipo: TipoDeMudanca }) {
  return <Etiqueta className={ESTILO_DE_TIPO[tipo]}>{ROTULO_DE_TIPO_DE_MUDANCA[tipo]}</Etiqueta>;
}

const ESTILO_DE_RISCO: Record<NivelDeRisco, string> = {
  LOW: 'bg-muted text-muted-foreground ring-border',
  MEDIUM: 'bg-secondary text-secondary-foreground ring-border',
  HIGH: 'bg-warning/15 text-warning ring-warning/30',
  VERY_HIGH: 'bg-destructive/12 text-destructive ring-destructive/25',
};

export function EtiquetaDeRisco({ nivel, escore }: { nivel: NivelDeRisco; escore?: number | null }) {
  return (
    <Etiqueta className={ESTILO_DE_RISCO[nivel]}>
      Risco {ROTULO_DE_RISCO[nivel].toLowerCase()}
      {typeof escore === 'number' ? (
        <span className="ml-1 tabular-nums opacity-80">· {escore}</span>
      ) : null}
    </Etiqueta>
  );
}

const ESTILO_DE_JANELA: Record<SituacaoDeJanela, string> = {
  PLANNED: 'bg-secondary text-secondary-foreground ring-border',
  CONFIRMED: 'bg-primary/12 text-primary ring-primary/25',
  IN_PROGRESS: 'bg-warning/15 text-warning ring-warning/30',
  COMPLETED: 'bg-success/12 text-success ring-success/25',
  CANCELLED: 'bg-muted text-muted-foreground ring-border line-through',
};

export function EtiquetaDeJanela({ situacao }: { situacao: SituacaoDeJanela }) {
  return <Etiqueta className={ESTILO_DE_JANELA[situacao]}>{ROTULO_DE_JANELA[situacao]}</Etiqueta>;
}

export function EtiquetaDeEnforcement({ enforcement }: { enforcement: Enforcement }) {
  return (
    <Etiqueta
      className={
        enforcement === 'BLOCKING'
          ? 'bg-destructive/12 text-destructive ring-destructive/25'
          : 'bg-warning/15 text-warning ring-warning/30'
      }
    >
      <Snowflake className="mr-1 h-3 w-3" aria-hidden />
      {ROTULO_DE_ENFORCEMENT[enforcement]}
    </Etiqueta>
  );
}

const ESTILO_DE_DESFECHO: Record<Desfecho, string> = {
  SUCCESSFUL: 'bg-success/12 text-success ring-success/25',
  SUCCESSFUL_WITH_ISSUES: 'bg-warning/15 text-warning ring-warning/30',
  FAILED: 'bg-destructive/12 text-destructive ring-destructive/25',
  ROLLED_BACK: 'bg-destructive/12 text-destructive ring-destructive/25',
};

export function EtiquetaDeDesfecho({ desfecho }: { desfecho: Desfecho }) {
  return <Etiqueta className={ESTILO_DE_DESFECHO[desfecho]}>{ROTULO_DE_DESFECHO[desfecho]}</Etiqueta>;
}

const ESTILO_DE_EXCECAO: Record<SituacaoDeExcecao, string> = {
  REQUESTED: 'bg-warning/15 text-warning ring-warning/30',
  APPROVED: 'bg-success/12 text-success ring-success/25',
  REJECTED: 'bg-destructive/12 text-destructive ring-destructive/25',
  WITHDRAWN: 'bg-muted text-muted-foreground ring-border',
};

export function EtiquetaDeExcecao({ situacao }: { situacao: SituacaoDeExcecao }) {
  return <Etiqueta className={ESTILO_DE_EXCECAO[situacao]}>{ROTULO_DE_EXCECAO[situacao]}</Etiqueta>;
}

// ── avisos ─────────────────────────────────────────────────────────────────

/**
 * O aviso de conflito, com a gravidade traduzida em tratamento.
 *
 * Bloqueante é vermelho e diz o que destrava; atenção é âmbar e **não** oferece
 * nada para clicar, porque não há o que resolver — a decisão é do comitê, e é
 * essa a diferença entre as duas coisas.
 */
export function AvisoDeConflito({
  gravidade,
  titulo,
  children,
  acao,
}: {
  gravidade: 'BLOQUEANTE' | 'ATENCAO';
  titulo: ReactNode;
  children?: ReactNode;
  acao?: ReactNode;
}) {
  const bloqueante = gravidade === 'BLOQUEANTE';
  const Icone = bloqueante ? ShieldAlert : AlertTriangle;
  return (
    <div
      className={cn(
        'flex flex-wrap items-start gap-3 rounded-md border px-3 py-2.5 text-sm',
        bloqueante
          ? 'border-destructive/30 bg-destructive/5 text-destructive'
          : 'border-warning/30 bg-warning/10 text-warning',
      )}
    >
      <Icone className="mt-0.5 h-4 w-4 flex-none" aria-hidden />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-semibold">{titulo}</p>
        {children ? <div className="text-foreground/90">{children}</div> : null}
      </div>
      {acao}
    </div>
  );
}

/**
 * "Salvo" — a confirmação que um botão de gravar silencioso não dá.
 *
 * Sem ela, salvar os planos de uma mudança é indistinguível de não salvar: a
 * tela fica exatamente igual, e a pessoa clica de novo, ou desiste e reescreve.
 * O aviso fica **até a próxima alteração**, e não some sozinho depois de três
 * segundos: quem estava lendo o plano quando clicou não olhou para o botão.
 */
export function Salvo({ visivel, texto = 'salvo' }: { visivel: boolean; texto?: string }) {
  if (!visivel) return null;
  return (
    <span role="status" className="inline-flex items-center gap-1 text-xs font-medium text-success">
      <Check className="h-3.5 w-3.5" aria-hidden />
      {texto}
    </span>
  );
}

// ── casca ──────────────────────────────────────────────────────────────────

export function Secao({
  titulo,
  descricao,
  acao,
  children,
}: {
  titulo: ReactNode;
  descricao?: ReactNode;
  acao?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h3 className="text-sm font-semibold tracking-tight">{titulo}</h3>
          {descricao ? <p className="max-w-2xl text-sm text-muted-foreground">{descricao}</p> : null}
        </div>
        {acao}
      </div>
      {children}
    </section>
  );
}

export function Campo({
  rotulo,
  htmlFor,
  dica,
  children,
}: {
  rotulo: ReactNode;
  htmlFor?: string;
  dica?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="block text-sm font-medium">
        {rotulo}
      </label>
      {children}
      {dica ? <p className="text-xs text-muted-foreground">{dica}</p> : null}
    </div>
  );
}

export const CLASSE_DE_CAMPO =
  'w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm ' +
  'transition-colors placeholder:text-muted-foreground ' +
  'hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background focus-visible:outline-none focus-visible:shadow-glow ' +
  'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground';

/** Um dado de leitura, no formato `termo / valor` das fichas do sistema. */
export function Dado({
  termo,
  valor,
  titulo,
}: {
  termo: string;
  valor: ReactNode;
  titulo?: string;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{termo}</dt>
      <dd className="mt-0.5 font-medium" title={titulo}>
        {valor}
      </dd>
    </div>
  );
}
